import { z } from 'zod';

import { isTrackingDisabled } from '@/shared/lib/isTrackingDisabled';

export const signupDecisionSchema = z
  .object({
    assignment_id: z.string().uuid().nullable(),
    key: z.literal('signup.page_v2_sep_2026'),
    participating: z.boolean(),
    variant: z.enum(['A', 'B', 'C']),
  })
  .refine(
    (decision) =>
      decision.participating
        ? decision.assignment_id !== null
        : decision.variant === 'A' && decision.assignment_id === null,
    { message: 'Inconsistent signup assignment' },
  );

export type SignupDecision = z.infer<typeof signupDecisionSchema>;
export const SIGNUP_CONTROL: SignupDecision = {
  assignment_id: null,
  key: 'signup.page_v2_sep_2026',
  participating: false,
  variant: 'A',
};
const STORAGE_KEY = 'rask.signupExperiment';

export const saveSignupDecision = (decision: SignupDecision) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(decision));
  } catch {
    /* Registration remains available when storage is blocked. */
  }
};

export const readSignupDecision = (): SignupDecision => {
  if (isTrackingDisabled()) return SIGNUP_CONTROL;
  try {
    return signupDecisionSchema.parse(JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null'));
  } catch {
    return SIGNUP_CONTROL;
  }
};

export const clearSignupDecision = () => saveSignupDecision(SIGNUP_CONTROL);

export const signupExperimentProperties = () => {
  const decision = readSignupDecision();
  return decision.participating && decision.assignment_id
    ? { signup_experiment_key: decision.key, signup_experiment_variant: decision.variant }
    : {};
};

const ASSIGNMENT_KEY = 'rask.signupExperiment.assignment.v1';
const EXCLUDED_KEY = 'rask.signupExperiment.excluded';
const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
const storedSchema = z.object({ decision: signupDecisionSchema, createdAt: z.number().finite() });

export const excludeSignupVisitor = () => {
  clearSignupDecision();
  try {
    localStorage.setItem(EXCLUDED_KEY, '1');
  } catch {
    /* Storage may be unavailable. */
  }
};

export const chooseSignupDecision = (): SignupDecision => {
  try {
    if (
      isTrackingDisabled() ||
      /bot|crawler|spider|headless|selenium|playwright/i.test(navigator.userAgent) ||
      localStorage.getItem(EXCLUDED_KEY)
    )
      return SIGNUP_CONTROL;
    const active = ['A', 'B', 'C'] as const;
    const parsed = storedSchema.safeParse(JSON.parse(localStorage.getItem(ASSIGNMENT_KEY) ?? 'null'));
    if (
      parsed.success &&
      parsed.data.decision.participating &&
      Date.now() - parsed.data.createdAt >= 0 &&
      Date.now() - parsed.data.createdAt < MAX_AGE
    ) {
      return parsed.data.decision;
    }
    const values = new Uint32Array(1);
    const limit = Math.floor(0x100000000 / active.length) * active.length;
    do {
      crypto.getRandomValues(values);
    } while (values[0] >= limit);
    const decision: SignupDecision = {
      ...SIGNUP_CONTROL,
      assignment_id: crypto.randomUUID(),
      participating: true,
      variant: active[values[0] % active.length],
    };
    const stored = JSON.stringify({ decision, createdAt: Date.now() });
    localStorage.setItem(ASSIGNMENT_KEY, stored);
    return localStorage.getItem(ASSIGNMENT_KEY) === stored ? decision : SIGNUP_CONTROL;
  } catch {
    return SIGNUP_CONTROL;
  }
};
